import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveRadar } from '@nivo/radar';
import { ReportCardComponentProps } from '../schema-types';
import { PaletteColor } from '../../../../lib/types';

// Standardized color scheme for all charts
const STANDARD_COLORS = {
  primary: PaletteColor.GREEN.hex,      // Green - for player/positive values
  secondary: PaletteColor.ORANGE.hex,   // Orange - for peer group/negative values
  palette: [
    PaletteColor.GREEN, PaletteColor.ORANGE, PaletteColor.BLUE, PaletteColor.PURPLE,
    PaletteColor.RED, PaletteColor.CYAN, PaletteColor.AMBER, PaletteColor.BROWN,
  ].map(({ hex }) => hex)
};

// Define chart data interfaces
export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface LegendItem {
  color: string;
  title: string;
}

export interface ChartProps {
  type: 'bar' | 'line' | 'pie' | 'donut' | 'radar' | 'horizontal-bar' | 'stacked-line';
  title?: string;
  question?: string;
  data: ChartDataPoint[];
  width?: number;
  height?: number;
  colors?: string[];
  yAxisLabel?: string;
  xAxisLabel?: string;
  maxValue?: number;
  overallScore?: number;
  playerName?: string;
  orderBy?: 'value' | 'difference' | 'none';
  orderDirection?: 'asc' | 'desc';
  showSectionHighlights?: boolean;
  peerGroupData?: ChartDataPoint[];
  peerGroupLabel?: string;
  containerWidth?: number;
  responsiveWidth?: boolean;
  hidePlainTextScores?: boolean;
  legendItems?: LegendItem[] | string[];
}

// Bar Chart Component using Nivo
const BarChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  xAxisLabel?: string;
  maxValue?: number;
  containerWidth?: number;
  legendItems?: LegendItem[];
}> = ({ data, height, colors, xAxisLabel, maxValue, legendItems }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  // Create label mapping for tooltips
  const labelMap = new Map();
  const nivoData = data.map((point, index) => {
    const truncated = truncateLabel(point.label);
    labelMap.set(truncated, point.label);
    return {
      id: truncated,
      originalLabel: point.label,
      value: point.value,
      color: point.color || colors[index % colors.length]
    };
  });

  // Calculate if labels should be rotated based on total label length
  const totalLabelLength = data.reduce((sum, point) => sum + point.label.length, 0);
  const averageLabelLength = totalLabelLength / data.length;
  const shouldRotateLabels = averageLabelLength > 5 || data.length > 8;

  // Calculate maximum label length for proper margin adjustment
  const maxLabelLength = Math.max(...data.map(point => point.label.length));
  const dynamicBottomMargin = shouldRotateLabels ?
    Math.max(80, Math.min(120, maxLabelLength * 4)) : 50;

  // Since Nivo doesn't support direct hover tooltips on axis labels,
  // we'll rely on the enhanced chart tooltip to show full labels

  // For bar charts with custom legend items, we'll need to render them separately
  // since Nivo bar charts generate legends from keys/indexes, not custom data

  const legendHeight = legendItems && legendItems.length > 0 ? 40 : 0;
  const chartHeight = height - legendHeight;

  return (
    <Box width="100%" height={height} style={{ overflow: 'visible', position: 'relative' }}>
      <Box width="100%" height={chartHeight} style={{ overflow: 'visible', position: 'relative' }}>
        <ResponsiveBar
        data={nivoData}
        keys={['value']}
        indexBy="id"
        margin={{
          top: 30,
          right: 30, // Increased right margin for tooltip space
          bottom: dynamicBottomMargin,
          left: 50
        }}
        // padding={0.3}
        minValue={0}
        maxValue={maxValue}
        colors={({ data }) => data.color}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: shouldRotateLabels ? -45 : 0,
          legend: xAxisLabel,
          legendPosition: 'middle',
          legendOffset: shouldRotateLabels ? dynamicBottomMargin - 10 : 32
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Score',
          legendPosition: 'middle',
          legendOffset: -40
        }}
        enableLabel={false}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
        theme={{
          tooltip: {
            container: {
              fontSize: 12,
              zIndex: 9999
            }
          }
        }}
        tooltip={({ id, value, data }) => (
          <Box
            style={{
              background: 'white',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              maxWidth: '200px',
              zIndex: 9999,
              position: 'relative'
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {(data as any).originalLabel}
            </Typography>
          </Box>
        )}
        />
      </Box>
      {/* Legend below chart */}
      {legendItems && legendItems.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{ marginTop: 8, flexWrap: 'wrap' }}
        >
          {legendItems.map((item, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="center"
              style={{ marginRight: 16, marginBottom: 4 }}
            >
              <Box
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '2px',
                  marginRight: 6
                }}
              />
              <Typography variant="caption" style={{ fontSize: '11px' }}>
                {item.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Horizontal Bar Chart Component using Nivo
const HorizontalBarChart: React.FC<{
  data: ChartDataPoint[];
  width?: number;
  height?: number;
  colors: string[];
  maxValue?: number;
  overallScore?: number;
  playerName?: string;
  orderBy?: 'value' | 'difference' | 'none';
  orderDirection?: 'asc' | 'desc';
  showSectionHighlights?: boolean;
  legendItems?: LegendItem[];
  hidePlainTextScores?: boolean;
}> = ({ data, height, colors, maxValue, overallScore, orderBy = 'difference', orderDirection = 'desc', showSectionHighlights = true, hidePlainTextScores, legendItems }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 8) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  // Calculate legend height and adjust chart height accordingly
  const legendHeight = legendItems && legendItems.length > 0 ? 40 : 0;
  const totalHeight = height || Math.max(data.length * 60 + 80, 200);
  const chartHeight = totalHeight - legendHeight;
  const max = maxValue || 100;
  const overallScoreValue = overallScore || (max / 2);

  // Calculate symmetric range around 0 for the diverging chart
  const maxDifference = Math.max(...data.map(d => Math.abs(d.value - overallScoreValue)));
  const chartPadding = maxDifference * 0.1;
  const chartMin = -(maxDifference + chartPadding);
  const chartMax = maxDifference + chartPadding;

  // Transform and sort data
  let processedData = data.map((point, index) => {
    const difference = point.value - overallScoreValue;
    const isPositive = difference >= 0;

    return {
      id: truncateLabel(point.label),
      label: truncateLabel(point.label),
      originalLabel: point.label, // Store original for tooltip
      // For positive differences: value extends right from 0 to +difference
      // For negative differences: value extends left from -difference to 0
      positive: isPositive ? difference : 0,
      negative: isPositive ? 0 : difference, // This will be negative
      actualValue: point.value,
      color: point.color || colors[index % colors.length],
      difference: difference,
      originalIndex: index
    };
  });

  // Sort data based on orderBy prop
  if (orderBy === 'difference') {
    // Default: Sort by difference (positive to negative)
    processedData = processedData.sort((a, b) => orderDirection === 'desc' ? a.difference - b.difference : b.difference - a.difference);
  } else if (orderBy === 'value') {
    // Sort by actual value
    processedData = processedData.sort((a, b) => orderDirection === 'desc' ? a.actualValue - b.actualValue : b.actualValue - a.actualValue);
  }
  // If orderBy === 'none', keep original order

  // Calculate sections for highlighting
  const topSkills = processedData.filter(d => d.difference > 0);
  const growthZone = processedData.filter(d => d.difference < 0);
  const topSkillsHeight = (Math.min(topSkills.length, 3) / (processedData.length + 1)) * 100;
  const growthZoneHeight = (Math.min(growthZone.length, 3) / (processedData.length + 1)) * 100;

  const nivoData = processedData;

console.log("Nivo data", {nivoData, isMobile})

  return (
    <Box width="100%" height={totalHeight} style={{ overflow: 'visible', position: 'relative' }}>
      <Box width="100%" height={chartHeight} style={{ overflow: 'visible', position: 'relative' }}>

      {/* Section Highlights */}
      {showSectionHighlights && topSkills.length > 0 && (
        <Box
          style={{
            position: 'absolute',
            top: isMobile ? 10 : 15, // Raise higher on mobile
            left: 0,
            right: 0,
            height: `${topSkillsHeight}%`,
            backgroundColor: alpha(STANDARD_COLORS.secondary, 0.1),
            borderRadius: '12px',
            border: `2px solid ${alpha(STANDARD_COLORS.secondary, 0.3)}`,
            zIndex: 1,
            pointerEvents: 'none'
          }}
        >
          <Box
            style={{
              position: 'absolute',
              top: 8, // Position back on graph, slightly down from top
              left: isMobile ? 65 : 85, // Position to left of y-axis labels on mobile with more margin
              backgroundColor: STANDARD_COLORS.secondary,
              color: 'white',
              padding: isMobile ? '2px 8px' : '4px 12px',
              borderRadius: '16px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '2px' : '4px' // Reduce gap on mobile
            }}
          >
            {!isMobile && <span style={{ fontSize: '16px' }}>🏆</span>} TOP SKILLS
          </Box>
        </Box>
      )}

      {showSectionHighlights && growthZone.length > 0 && (
        <Box
          style={{
            position: 'absolute',
            bottom: 50,
            left: 0,
            right: 0,
            height: `${growthZoneHeight}%`,
            backgroundColor: alpha(STANDARD_COLORS.primary, 0.1),
            borderRadius: '12px',
            border: `2px solid ${alpha(STANDARD_COLORS.primary, 0.3)}`,
            zIndex: 1,
            pointerEvents: 'none'
          }}
        >
          <Box
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              backgroundColor: STANDARD_COLORS.primary,
              color: 'white',
              padding: isMobile ? '2px 8px' : '4px 12px',
              borderRadius: '16px',
              fontSize: isMobile ? '10px' : '12px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '2px' : '4px' // Reduce gap on mobile
            }}
          >
            {!isMobile && <span style={{ fontSize: '16px' }}>🎯</span>} GROWTH ZONE
          </Box>
        </Box>
      )}

      <ResponsiveBar
        data={nivoData}
        keys={['negative', 'positive']}
        indexBy="id"
        layout="horizontal"
        margin={{ top: 10, right: 70, bottom: 50, left: 70 }}
        padding={0.2}
        minValue={chartMin}
        maxValue={chartMax}
        colors={({ id, data }) => {
          // Use different colors for positive and negative differences
          if (id === 'positive') {
            return (data as any).color || STANDARD_COLORS.primary; // Green for above overall
          } else if (id === 'negative') {
            return (data as any).color || STANDARD_COLORS.secondary; // Orange for below overall
          }
          return '#ccc';
        }}
        borderWidth={1}
        borderColor="#333"
        borderRadius={12}
        axisTop={null}
        axisRight={{
          tickSize: 0,
          tickPadding: 0,
          tickRotation: 0,
          format: (value) => {
            const numValue = Number(value);
            if (isNaN(numValue)) return '';
            if (numValue === 0) {
              return '0';
            }
            return numValue > 0 ? `+${numValue}` : `-${numValue}`;
          },
          legendPosition: 'middle',
          legendOffset: 32
        }}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          tickValues: isMobile ? 4 : undefined, // Reduce ticks on mobile
          format: (value) => {
            const numValue = Number(value);
            if (isNaN(numValue)) return '';
            if (numValue === 0) return '0';
            return numValue > 0 ? `+${numValue}` : `-${numValue}`;
          },
          legend: 'Difference from Overall',
          legendPosition: 'middle',
          legendOffset: 32
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: 0,
          legendPosition: 'middle',
          legendOffset: -40
        }}
        enableGridX={true}
        enableGridY={false}
        enableLabel={false}
        labelTextColor="#333"
        animate={true}
        motionStiffness={90}
        motionDamping={15}
        theme={{
          axis: {
            ticks: {
              text: {
                fontSize: 11,
                fontWeight: 'bold',
                fill: '#333'
              }
            }
          },
          grid: {
            line: {
              stroke: '#e0e0e0',
              strokeWidth: 1,
              strokeDasharray: '2,2'
            }
          },
          tooltip: {
            container: {
              fontSize: 12,
              zIndex: 9999
            }
          }
        }}
        tooltip={({ data }) => (
          <Box
            style={{
              background: 'white',
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 9999,
              position: 'relative'
            }}
          >
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {(data as any).originalLabel || (data as any).label}
            </Typography>
            {!hidePlainTextScores && <>
              <Typography variant="body2">
                Score: {(data as any).actualValue}
              </Typography>
              <Typography variant="body2" style={{ fontSize: '10px', color: '#666' }}>
                {(data as any).actualValue >= overallScoreValue ? `+${((data as any).actualValue - overallScoreValue).toFixed(1)}` : `${((data as any).actualValue - overallScoreValue).toFixed(1)}`} vs Overall
              </Typography>
            </>}
          </Box>
        )}
        markers={[
          {
            axis: 'x',
            value: 0,
            lineStyle: { stroke: '#000', strokeWidth: 2 },
          }
        ]}
      />
      </Box>

      {/* Legend below chart */}
      {legendItems && legendItems.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          {legendItems.map((item, index) => (
            <Box
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginRight: index < legendItems.length - 1 ? '16px' : 0
              }}
            >
              <Box
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  marginRight: '6px',
                  borderRadius: '2px'
                }}
              />
              <Typography variant="caption" style={{ fontSize: '11px' }}>
                {item.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Pie Chart Component using Nivo
const PieChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  containerWidth?: number;
  hidePlainTextScores?: boolean;
}> = ({ data, height, colors, hidePlainTextScores }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  const nivoData = data.map((point, index) => ({
    id: truncateLabel(point.label),
    label: truncateLabel(point.label),
    originalLabel: point.label,
    value: point.value,
    color: point.color || colors[index % colors.length]
  }));

  // Calculate if labels should be skipped based on total label length and number of items
  const totalLabelLength = data.reduce((sum, point) => sum + point.label.length, 0);
  const averageLabelLength = totalLabelLength / data.length;
  const shouldSkipLabels = averageLabelLength > 12 || data.length > 8;

  return (
    <Box width="100%" height={height} style={{ overflow: 'visible' }}>
      <ResponsivePie
        data={nivoData}
        margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
        innerRadius={0.5}
        padAngle={0.7}
        cornerRadius={3}
        colors={({ data }) => (data as any).color}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
        radialLabelsSkipAngle={shouldSkipLabels ? 30 : 10}
        radialLabelsTextColor="#333333"
        radialLabelsLinkColor={{ from: 'color' }}
        slicesLabelsSkipAngle={shouldSkipLabels ? 30 : 10}
        slicesLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
        theme={{
          tooltip: {
            container: {
              fontSize: 12,
              zIndex: 9999
            }
          }
        }}
        tooltip={(datum) => {
          const typedDatum = datum as any;
          return (
            <Box
              style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '200px',
                zIndex: 9999,
                position: 'relative'
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {typedDatum.originalLabel}
              </Typography>
              {!hidePlainTextScores && <>
                <Typography variant="body2">
                  Value: {typedDatum.value}
                </Typography>
                <Typography variant="body2" style={{ fontSize: '10px', color: '#666' }}>
                  {((typedDatum.value / nivoData.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}%
                </Typography>
              </>}
            </Box>
          );
        }}
      />
    </Box>
  );
};

// Line Chart Component using Nivo
const LineChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  peerGroupData?: ChartDataPoint[];
  peerGroupLabel?: string;
  playerName?: string;
  containerWidth?: number;
  hidePlainTextScores?: boolean;
  xAxisLabel?: string;
}> = ({ data, height, colors, peerGroupData, peerGroupLabel = 'Peer Group', playerName = 'Player', hidePlainTextScores, xAxisLabel = "Skill Groups" }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  // Store original labels for tooltip
  const labelMap = new Map();
  const processedData = data.map(point => {
    const truncated = truncateLabel(point.label);
    labelMap.set(truncated, point.label);
    return {
      ...point,
      label: truncated
    };
  });

  // Create line series for player and peer group data
  const nivoData = [{
    id: 'player',
    color: STANDARD_COLORS.primary,
    data: processedData.map(point => ({
      x: point.label,
      y: point.value
    }))
  }];

  // Add peer group data if available
  if (peerGroupData && peerGroupData.length > 0) {
    const processedPeerData = peerGroupData.map(point => {
      const truncated = truncateLabel(point.label);
      labelMap.set(truncated, point.label);
      return {
        ...point,
        label: truncated
      };
    });

    nivoData.push({
      id: 'peerGroup',
      color: STANDARD_COLORS.secondary,
      data: processedPeerData.map(point => ({
        x: point.label,
        y: point.value
      }))
    });
  }

  // Calculate if labels should be rotated based on total label length
  const totalLabelLength = data.reduce((sum, point) => sum + point.label.length, 0);
  const averageLabelLength = totalLabelLength / data.length;
  const shouldRotateLabels = averageLabelLength > 5 || data.length > 8;

  // Calculate maximum label length for proper margin adjustment
  const maxLabelLength = Math.max(...data.map(point => point.label.length));
  const dynamicBottomMargin = shouldRotateLabels ?
    Math.max(80, Math.min(120, maxLabelLength * 4)) : 50;

  return (
    <Box width="100%" style={{ overflow: 'visible' }}>
      <Box height={height} width="100%">
        <ResponsiveLine
          data={nivoData}
          margin={{
            top: 30,
            right: 30, // Consistent right margin
            bottom: dynamicBottomMargin,
            left: 50
          }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
          yFormat=" >-.2f"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: shouldRotateLabels ? -45 : 0,
            legend: xAxisLabel,
            legendOffset: shouldRotateLabels ? dynamicBottomMargin - 10 : 36,
            legendPosition: 'middle'
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Score',
            legendOffset: -40,
            legendPosition: 'middle'
          }}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          colors={({ id }) => nivoData.find(series => series.id === id)?.color || colors[0]}
          theme={{
            tooltip: {
              container: {
                fontSize: 12,
                zIndex: 9999
              }
            }
          }}
          tooltip={({ point }) => (
            <Box
              style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '200px',
                zIndex: 9999,
                position: 'relative'
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {labelMap.get(point.data.x) || point.data.x}
              </Typography><Typography variant="body2">
                {point.serieId === 'player' ? playerName : peerGroupLabel}{!hidePlainTextScores ? ": " + point.data.y : ""}
              </Typography>
            </Box>
          )}
        />
      </Box>
      {peerGroupData && peerGroupData.length > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" style={{ marginRight: 16, marginBottom: 4 }}>
            <Box
              style={{
                width: 12,
                height: 12,
                backgroundColor: STANDARD_COLORS.primary,
                borderRadius: '50%',
                marginRight: 4
              }}
            />
            <Typography variant="caption" style={{ color: '#333' }}>{playerName}</Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ marginBottom: 4 }}>
            <Box
              style={{
                width: 12,
                height: 12,
                backgroundColor: STANDARD_COLORS.secondary,
                borderRadius: '50%',
                marginRight: 4
              }}
            />
            <Typography variant="caption" style={{ color: '#333', wordBreak: 'break-word' }}>
              {peerGroupLabel}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Donut Chart Component using Nivo
const DonutChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  containerWidth?: number;
  hidePlainTextScores?: boolean;
}> = ({ data, height, colors, hidePlainTextScores }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  const nivoData = data.map((point, index) => ({
    id: truncateLabel(point.label),
    label: truncateLabel(point.label),
    originalLabel: point.label,
    value: parseFloat(((point.value / data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)),
    color: point.color || colors[index % colors.length]
  }));

  // Calculate if labels should be skipped based on total label length and number of items
  const totalLabelLength = data.reduce((sum, point) => sum + point.label.length, 0);
  const averageLabelLength = totalLabelLength / data.length;
  const shouldSkipLabels = averageLabelLength > 12 || data.length > 8;

  return (
    <Box width="100%" height={height} style={{ overflow: 'visible' }}>
      <ResponsivePie
        data={nivoData}
        margin={{ top: 20, bottom: 30 }}
        innerRadius={0.7} // Key difference: larger inner radius for donut
        padAngle={0.7}
        cornerRadius={3}
        colors={(data) => (data as any).color}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
        radialLabelsSkipAngle={shouldSkipLabels ? 30 : 10}
        radialLabelsTextColor="#333333"
        radialLabelsLinkColor={{ from: 'color' }}
        slicesLabelsSkipAngle={shouldSkipLabels ? 30 : 10}
        slicesLabelsTextColor="#ffffff"
        animate={true}
        motionStiffness={90}
        enableRadialLabels={!shouldSkipLabels}
        enableSlicesLabels={!shouldSkipLabels && !hidePlainTextScores}
        motionDamping={15}
        theme={{
          tooltip: {
            container: {
              fontSize: 12,
              zIndex: 9999
            }
          }
        }}
        tooltip={(datum) => {
          const typedDatum = datum as any;
          return (
            <Box
              style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '200px',
                zIndex: 9999,
                position: 'relative'
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {typedDatum.originalLabel}
              </Typography>
              {!hidePlainTextScores && <>
                <Typography variant="body2">
                  Value: {typedDatum.value}
                </Typography>
                <Typography variant="body2" style={{ fontSize: '10px', color: '#666' }}>
                  {typedDatum.value.toFixed(1)}%
                </Typography>
              </>}
            </Box>
          );
        }}
      />
    </Box>
  );
};

// Stacked Line Chart Component using Nivo
const StackedLineChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  peerGroupData?: ChartDataPoint[];
  peerGroupLabel?: string;
  playerName?: string;
  containerWidth?: number;
  hidePlainTextScores?: boolean;
  xAxisLabel?: string;
  stackedGroups?: string[]; // Array of group names to stack
}> = ({ data, height, colors, peerGroupData, peerGroupLabel = 'Peer Group', playerName = 'Player', hidePlainTextScores, xAxisLabel = "Time Periods", stackedGroups = [] }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  // Store original labels for tooltip
  const labelMap = new Map();
  const processedData = data.map(point => {
    const truncated = truncateLabel(point.label);
    labelMap.set(truncated, point.label);
    return {
      ...point,
      label: truncated
    };
  });

  // Create multiple line series for stacked data
  // If stackedGroups is provided, create separate series for each group
  const nivoData: Array<{id: string; color: string; data: Array<{x: string; y: number}>}> = [];

  if (stackedGroups.length > 0) {
    // Create stacked series based on provided groups
    stackedGroups.forEach((groupName, index) => {
      const groupData = processedData.filter(point =>
        point.label.toLowerCase().includes(groupName.toLowerCase()) ||
        (point as any).group === groupName
      );

      if (groupData.length > 0) {
        nivoData.push({
          id: groupName,
          color: colors[index % colors.length],
          data: groupData.map(point => ({
            x: point.label,
            y: point.value
          }))
        });
      }
    });
  } else {
    // Default: Create player series and peer group series
    nivoData.push({
      id: playerName,
      color: STANDARD_COLORS.primary,
      data: processedData.map(point => ({
        x: point.label,
        y: point.value
      }))
    });

    // Add peer group data if available
    if (peerGroupData && peerGroupData.length > 0) {
      const processedPeerData = peerGroupData.map(point => {
        const truncated = truncateLabel(point.label);
        labelMap.set(truncated, point.label);
        return {
          ...point,
          label: truncated
        };
      });

      nivoData.push({
        id: peerGroupLabel,
        color: STANDARD_COLORS.secondary,
        data: processedPeerData.map(point => ({
          x: point.label,
          y: point.value
        }))
      });
    }
  }

  // Calculate if labels should be rotated based on total label length
  const totalLabelLength = data.reduce((sum, point) => sum + point.label.length, 0);
  const averageLabelLength = totalLabelLength / data.length;
  const shouldRotateLabels = averageLabelLength > 5 || data.length > 8;

  // Calculate maximum label length for proper margin adjustment
  const maxLabelLength = Math.max(...data.map(point => point.label.length));
  const dynamicBottomMargin = shouldRotateLabels ?
    Math.max(80, Math.min(120, maxLabelLength * 4)) : 50;

  return (
    <Box width="100%" style={{ overflow: 'visible' }}>
      <Box height={height} width="100%">
        <ResponsiveLine
          data={nivoData}
          margin={{
            top: 30,
            right: 30,
            bottom: dynamicBottomMargin,
            left: 50
          }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: true, reverse: false }} // Key difference: stacked: true
          yFormat=" >-.2f"
          curve="monotoneX" // Smoother curves for stacked lines
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: shouldRotateLabels ? -45 : 0,
            legend: xAxisLabel,
            legendOffset: shouldRotateLabels ? dynamicBottomMargin - 10 : 36,
            legendPosition: 'middle'
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Cumulative Score',
            legendOffset: -40,
            legendPosition: 'middle'
          }}
          pointSize={6}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={true}
          enableArea={true} // Fill areas for stacked effect
          areaOpacity={0.3}
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          colors={({ id }) => nivoData.find(series => series.id === id)?.color || colors[0]}
          theme={{
            tooltip: {
              container: {
                fontSize: 12,
                zIndex: 9999
              }
            }
          }}
          tooltip={({ point }) => (
            <Box
              style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                maxWidth: '200px',
                zIndex: 9999,
                position: 'relative'
              }}
            >
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {labelMap.get(point.data.x) || point.data.x}
              </Typography>
              <Typography variant="body2">
                {point.serieId}{!hidePlainTextScores ? ": " + point.data.y : ""}
              </Typography>
            </Box>
          )}
        />
      </Box>
      {nivoData.length > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          {nivoData.map((series, index) => (
            <Box key={series.id} display="flex" alignItems="center" style={{ marginRight: 16, marginBottom: 4 }}>
              <Box
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: series.color,
                  borderRadius: '50%',
                  marginRight: 4
                }}
              />
              <Typography variant="caption" style={{ color: '#333' }}>{series.id}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Radar Chart Component using Nivo
const RadarChart: React.FC<{
  data: ChartDataPoint[];
  width: number;
  height: number;
  colors: string[];
  peerGroupData?: ChartDataPoint[];
  peerGroupLabel?: string;
  playerName?: string;
  containerWidth?: number;
  playerLabel?: string;
  hidePlainTextScores?: boolean;
}> = ({ data, height, colors, peerGroupData, peerGroupLabel = 'Peer Group', playerName = 'Player', hidePlainTextScores }) => {
  // Helper function to truncate long labels
  const truncateLabel = (label: string, maxLength: number = 12) => {
    if (label.length <= maxLength) return label;

    const words = label.split(' ');
    if (words.length === 1) {
      // Single word: truncate to maxLength and add ellipsis
      return label.substring(0, maxLength) + '...';
    }

    // Multiple words: abbreviate leading words, keep last word full
    const lastWord = words[words.length - 1];
    const leadingWords = words.slice(0, -1);

    // Create abbreviated version
    const abbreviated = leadingWords.map(word => word.charAt(0) + '.').join(' ') + ' ' + lastWord;

    // If abbreviated version fits within maxLength, return it
    if (abbreviated.length <= maxLength) {
      return abbreviated;
    }

    // If still too long, truncate to maxLength and add ellipsis
    return abbreviated.substring(0, maxLength) + '...';
  };

  // Combine player data with peer group data using dynamic key names
  const playerKey = playerName || 'Player';
  const peerGroupKey = peerGroupLabel || 'Peer Group';

  const radarData = data.map(point => {
    const peerPoint = peerGroupData?.find(p => p.label === point.label);
    const dataPoint: any = {
      skill: truncateLabel(point.label),
      originalSkill: point.label, // Store original for tooltips
      [playerKey]: point.value
    };

    if (peerPoint && peerPoint.value !== null) {
      dataPoint[peerGroupKey] = peerPoint.value;
    }

    return dataPoint;
  });

  // Filter out null peer group values for keys
  const keys = peerGroupData && peerGroupData.length > 0 ? [playerKey, peerGroupKey] : [playerKey];

  return (
    <Box width="100%" style={{ overflow: 'visible' }}>
      <Box height={height} width="100%">
        <ResponsiveRadar
          data={radarData}
          keys={keys}
          indexBy="skill"
          maxValue="auto"
          margin={{
            top: 0,
            right: 100,
            bottom: keys.length > 1 ? 0 : 80,
            left: 100
          }}
          curve="linearClosed"
          borderWidth={2}
          borderColor={{ from: 'color' }}
          gridLabelOffset={36}
          dotSize={8}
          dotColor={{ theme: 'background' }}
          dotBorderWidth={2}
          colors={keys.length > 1 ? [STANDARD_COLORS.primary, STANDARD_COLORS.secondary] : [colors[0]]}
          fillOpacity={0.25}
          blendMode="multiply"
          animate={true}
          motionStiffness={90}
          motionDamping={15}
          legends={[]}
          tooltipFormat={(value) => {
            if (hidePlainTextScores) return null;
            return (
              <Box style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 9999,
                position: 'relative'
              }}>
                <Typography variant="body2">
                  Score: {value}
                </Typography>
              </Box>
            );
          }}
        />
      </Box>
      {keys.length > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" style={{ marginTop: 8, flexWrap: 'wrap' }}>
          <Box display="flex" alignItems="center" style={{ marginRight: 16, marginBottom: 4 }}>
            <Box
              style={{
                width: 12,
                height: 12,
                backgroundColor: STANDARD_COLORS.primary,
                borderRadius: '50%',
                marginRight: 4
              }}
            />
            <Typography variant="caption" style={{ color: '#333' }}>{playerKey}</Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ marginBottom: 4 }}>
            <Box
              style={{
                width: 12,
                height: 12,
                backgroundColor: STANDARD_COLORS.secondary,
                borderRadius: '50%',
                marginRight: 4
              }}
            />
            <Typography variant="caption" style={{ color: '#333', wordBreak: 'break-word' }}>
              {peerGroupKey}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Main Chart Component
export const ChartComponent: React.FC<ReportCardComponentProps & { chartConfig?: ChartProps }> = ({
  baseData,
  data,
  chartConfig,
  hidePlainTextScores = true
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Extract chart configuration from data or chartConfig prop
  const config: ChartProps = {
    type: 'bar',
    width: 600,
    height: 400,
    colors: STANDARD_COLORS.palette,
    responsiveWidth: true,
    ...chartConfig,
    ...data
  };

  const {
    type,
    title,
    question,
    data: chartData = [],
    height = 400,
    colors = STANDARD_COLORS.palette,
    xAxisLabel,
    yAxisLabel,
    maxValue,
    overallScore,
    playerName = config.playerName || data?.playerName || data?.playerLabel || baseData?.PLAYER_FIRST_NAME,
    orderBy = 'difference',
    showSectionHighlights = false,
    peerGroupData,
    peerGroupLabel = 'Peer Group',
    legendItems,
  } = config;

  // Validate and process legend items
  const processedLegendItems: LegendItem[] | undefined = React.useMemo(() => {
    if (!legendItems || !Array.isArray(legendItems)) return undefined;

    // If it's an array of strings (incorrect format), ignore it
    if (legendItems.length > 0 && typeof legendItems[0] === 'string') {
      console.warn('Legend items should be an array of objects with color and title properties, not strings');
      return undefined;
    }

    // Type guard to ensure we're working with LegendItem[]
    const items = legendItems as LegendItem[];

    // Validate that each item has color and title
    const validItems = items.filter((item): item is LegendItem => {
      return typeof item === 'object' &&
             item !== null &&
             'color' in item &&
             'title' in item &&
             typeof item.color === 'string' &&
             typeof item.title === 'string';
    });

    if (validItems.length !== items.length) {
      console.warn('Some legend items are missing required properties (color, title)');
    }

    return validItems.length > 0 ? validItems : undefined;
  }, [legendItems]);

  // Calculate responsive dimensions - always fill available space
  const getResponsiveDimensions = () => {
    const baseHeight = type === 'horizontal-bar' ? Math.max(chartData.length * 60 + 120, height) : height;

    if (isMobile) {
      return {
        height: Math.min(baseHeight * 0.8, 300), // Smaller height on mobile
        containerMaxWidth: '100%'
      };
    } else if (isTablet) {
      return {
        height: Math.min(baseHeight * 0.9, 400),
        containerMaxWidth: '100%'
      };
    } else {
      return {
        height: baseHeight,
        containerMaxWidth: '100%'
      };
    }
  };

  const responsiveDimensions = getResponsiveDimensions();

  const renderChart = () => {
    // Create a constrained container that the ResponsiveChart components will fill
    const ChartContainer = ({ children, additionalHeight }: { children: React.ReactNode; additionalHeight?: number }) => (
      <Box
        style={{
          width: '100%',
          height: responsiveDimensions.height + (additionalHeight || 0),
          maxWidth: responsiveDimensions.containerMaxWidth,
          overflow: 'visible', // Allow tooltips to overflow
          position: 'relative'
        }}
      >
        {children}
      </Box>
    );

    switch (type) {
      case 'bar':
        return (
          <ChartContainer>
            <BarChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              xAxisLabel={xAxisLabel}
              maxValue={maxValue}
              containerWidth={undefined} // Let container handle width
              legendItems={processedLegendItems}
            />
          </ChartContainer>
        );
      case 'horizontal-bar':
        return (
          <ChartContainer>
            <HorizontalBarChart
              data={chartData}
              height={responsiveDimensions.height}
              colors={colors}
              maxValue={maxValue}
              overallScore={overallScore}
              playerName={playerName}
              orderBy={orderBy}
              showSectionHighlights={showSectionHighlights}
              hidePlainTextScores={hidePlainTextScores}
              legendItems={processedLegendItems}
            />
          </ChartContainer>
        );
      case 'pie':
        return (
          <ChartContainer>
            <PieChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              containerWidth={undefined} // Let container handle width
              hidePlainTextScores={hidePlainTextScores}
            />
          </ChartContainer>
        );
      case 'donut':
        return (
          <ChartContainer>
            <DonutChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              containerWidth={undefined} // Let container handle width
              hidePlainTextScores={hidePlainTextScores}
            />
          </ChartContainer>
        );
      case 'stacked-line':
        return (
          <ChartContainer additionalHeight={80}>
            <StackedLineChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              peerGroupData={peerGroupData}
              peerGroupLabel={peerGroupLabel}
              playerName={playerName}
              containerWidth={undefined} // Let container handle width
              hidePlainTextScores={hidePlainTextScores}
              xAxisLabel={xAxisLabel}
              stackedGroups={(config as any).stackedGroups}
            />
          </ChartContainer>
        );
      case 'line':
        return (
          <ChartContainer additionalHeight={80}>
            <LineChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              peerGroupData={peerGroupData}
              peerGroupLabel={peerGroupLabel}
              playerName={playerName}
              containerWidth={undefined} // Let container handle width
              hidePlainTextScores={hidePlainTextScores}
              xAxisLabel={xAxisLabel}
            />
          </ChartContainer>
        );
      case 'radar':
        return (
          <ChartContainer additionalHeight={80}>
            <RadarChart
              data={chartData}
              width={600} // Will be constrained by container
              height={responsiveDimensions.height}
              colors={colors}
              peerGroupData={peerGroupData}
              peerGroupLabel={peerGroupLabel}
              playerName={playerName}
              containerWidth={undefined} // Let container handle width
              hidePlainTextScores={hidePlainTextScores}
            />
          </ChartContainer>
        );
      default:
        return (
          <Box
            width="100%"
            height={responsiveDimensions.height}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography>Chart type "{type}" not yet implemented</Typography>
          </Box>
        );
    }
  };

  return (
    <Box style={{ width: '100%', margin: '0 auto', overflow: 'visible' }}>
      {title && !question && (
        <Typography
          variant={isMobile ? "body1" : "h6"}
          align="center"
          gutterBottom
          style={{
            fontWeight: 700,
            fontSize: isMobile ? '1rem' : '1.25rem',
            marginBottom: isMobile ? '8px' : '16px'
          }}
        >
          {title}
        </Typography>
      )}
      <Box style={{ width: '100%', overflow: 'visible' }}>
        {renderChart()}
      </Box>
    </Box>
  );
};
